import { Alert, Autocomplete, Avatar, Backdrop, Box, Button, Card, CardContent, Chip, CircularProgress, IconButton, Stack, TextField, Typography } from "@mui/material"
import { alpha } from "@mui/material/styles"

import { Sell, SellPaymentMethod } from "../sell.entity";
import { SwalReact } from "../../common/swal-react";
import { AddProductSell } from "./addproduct.sell";
import { Product, noImage } from "../../product/product.entity";
import { productRepository } from "../../product/product.repository";
import { useCallback, useEffect, useMemo, useState } from "react";

import './sell-component.scss';
import { APP_DRAWER_WIDTH } from "../../common/layout-constants";
import { SellProduct } from "../sell-product";
import { FinishSellComponent } from "./finish-sell.component";
import { sellRepository } from "../sell.repository";
import { SaveSellComponent } from "./savesell-component";
import { Cancel, CheckCircle, Delete, Edit, Save } from "@mui/icons-material";
import { Shell } from "../../common/shell/shell.page";
import { AddByNameComponent } from "./add-by-name-component";
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import Swal from "sweetalert2";
import { MoneyInput } from "../../common/components/money-input";
import ValueObjectObservable from "../../common/vo/value-object-observable";
import { MoneyInputValueObject } from "../../common/components/money-input-value-object";
import { productCategoriesRepository } from "../../product/product-categories.repository";
import { BarcodeScanner } from "../../common/components/barcode-scanner";
import { SellUnregisterProduct } from "../sell-unregister-product";
import { v4 } from "uuid";
import { useModuleAccess } from "../../settings/module-access";
import { SellCopilotPanel, COPILOT_PANEL_WIDTH } from "./sell-copilot-panel";
import { SmartSellInput } from "./sell-smart-input";
import { SellProductPickerDialog } from "./sell-product-picker-dialog";
import { inferRegisterPrefillFromSearch } from "../../product/utils/register-prefill";
import { customerRepository } from "../../customer/customer.repository";
import { Customer } from "../../customer/customer.entity";
import { customerCreditService } from "../../customer/customer-credit.service";
import application from "../../core/application";
import { Permission } from "../../users/user.entity";

type SellComponentProps = {
    sellId: string;
    closeSell: () => void;
    /** Sessão de caixa atual (fluxo de caixa); propagado à venda ao salvar */
    cashSessionId?: string | null;
}

export const SellComponent = ({ sellId, closeSell, cashSessionId = null }: SellComponentProps) => {
    const [cancelFocusAddInput, setCancelFocusAddInput] = useState(false);
    const [removeMode, setRemoveMode] = useState(false);
    const [loading, setLoading] = useState(false);
    const [isFinishSell, setIsFinishSell] = useState(false);
    const moduleAccess = useModuleAccess();
    const [user] = application.user.useState();
    const hasProductsCatalog =
        !moduleAccess.loading && moduleAccess.activeProductsTier !== null;
    const [pickOpen, setPickOpen] = useState(false);
    const [pickTerm, setPickTerm] = useState("");
    const [customers, setCustomers] = useState<Customer[]>([]);
    const canCreateCustomerInSell =
        (user?.hasPermission([Permission.MANAGE_CUSTOMERS, Permission.ALLOW_CREDIT_SALE]) ?? false) &&
        moduleAccess.activeCustomersTier !== null;

    const [sell] = sellRepository.useFindById(sellId, () => {
        closeSell();
    });

    const loadCustomers = useCallback(async () => {
        const list = await customerRepository.listByName(500);
        setCustomers(list);
    }, []);

    useEffect(() => {
        void loadCustomers();
    }, [loadCustomers]);

    useEffect(() => {
        if (!sell) return;
        if (sell.getPaymentMethod() !== SellPaymentMethod.UNPAID && !isFinishSell) return closeSell();
    }, [sell, closeSell]);

    useEffect(() => {
        if (!sell || !cashSessionId) return;
        if (sell.getCashSessionId() === cashSessionId) return;
        sell.setCashSessionId(cashSessionId);
        sellRepository.save(sell);
    }, [sell, cashSessionId]);

    const addProduct = async (raw: string) => {
        if (!sell) return;
        const term = raw.trim();
        if (!term) return;

        if (removeMode) {
            const resolved = await productRepository.findByCodeOrLegacyDocumentId(term);
            sell.removeProduct(resolved?.id ?? term);
            await sellRepository.save(sell);
            return;
        }

        if (term === "1") {
            return addOnlyPrice();
        }

        setCancelFocusAddInput(true);

        try {
            setLoading(true);
            const resolved = await productRepository.findByCodeOrLegacyDocumentId(term);
            if (resolved) {
                sell.addProduct(new SellProduct(resolved, 1));
                await sellRepository.save(sell);
                setLoading(false);
                setCancelFocusAddInput(false);
                return;
            }
            const fuzzy = await productRepository.searchProductsForSell(term, 25);
            if (fuzzy.length === 1) {
                sell.addProduct(new SellProduct(fuzzy[0], 1));
                await sellRepository.save(sell);
                setLoading(false);
                setCancelFocusAddInput(false);
                return;
            }
            setLoading(false);
            setPickTerm(term);
            setPickOpen(true);
        } catch (err) {
            setLoading(false);
            setCancelFocusAddInput(false);
            console.error(err);
            await Swal.fire({
                title: "Erro",
                text: "Não foi possível consultar o produto.",
                icon: "error",
            });
        }
    };

    const finishSell = useCallback(() => {
        if (!sell) return;
        /*setCancelFocusAddInput(true);
        const finishModal = SwalReact.mixin({
            html: <FinishSellComponent sell={sell} onCompleteSell={() => {
                finishModal.close();
                closeSell();
            }} onClose={() => finishModal.close()} />,
            showConfirmButton: false,
            allowOutsideClick: false,
            showCloseButton: true,
            willClose: () => setCancelFocusAddInput(false)
        });
        finishModal.fire();*/
        setIsFinishSell(true);
    }, [closeSell, sell]);

    const addProductByName = useCallback(() => {
        if (!sell) return;
        setCancelFocusAddInput(true);
        const addProductByNameModal = SwalReact.mixin({
            html: <AddByNameComponent sell={sell} onClose={() => addProductByNameModal.close()} />,
            showConfirmButton: false,
            allowOutsideClick: true,
            showCloseButton: true,
            willClose: () => setCancelFocusAddInput(false)
        });
        addProductByNameModal.fire();
    }, [sell]);

    const addProductById = useCallback(async (productId: string) => {
        if (!sell) return;
        const product = await productRepository.findByIdOrNull(productId);
        if (!product) return;
        sell.addProduct(new SellProduct(product, 1));
        await sellRepository.save(sell);
    }, [sell]);

    const saveSell = useCallback(() => {
        if (!sell) return;
        setCancelFocusAddInput(true);
        const saveModal = SwalReact.mixin({
            html: <SaveSellComponent
                sell={sell}
                closeSave={() => saveModal.close()}
                onSellSaved={() => { saveModal.close(); closeSell() }}
            />,
            showConfirmButton: false,
            allowOutsideClick: true,
            showCloseButton: true,
            willClose: () => setCancelFocusAddInput(false)
        });
        saveModal.fire();
    }, [closeSell, sell]);

    const quickCreateCustomer = useCallback(async () => {
        if (!sell) return;
        const customersLimit = moduleAccess.limits.customersLimit;
        if (Number.isFinite(customersLimit) && customers.length >= customersLimit) {
            await Swal.fire({
                title: "Limite de clientes atingido",
                text: "Seu plano atual atingiu o limite de clientes. Faça upgrade do módulo Clientes e Fiado para cadastrar mais.",
                icon: "warning",
            });
            return;
        }
        setCancelFocusAddInput(true);
        const result = await SwalReact.fire({
            title: "Cliente rápido",
            html: (
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <TextField id="quickCustomerName" label="Nome" fullWidth autoFocus />
                    <TextField id="quickCustomerPhone" label="Telefone" fullWidth />
                </Stack>
            ),
            showCancelButton: true,
            confirmButtonText: "Salvar",
            cancelButtonText: "Cancelar",
            preConfirm: () => {
                const name = (document.getElementById("quickCustomerName") as HTMLInputElement)?.value?.trim();
                const phone = (document.getElementById("quickCustomerPhone") as HTMLInputElement)?.value?.trim() || "";
                if (!name) {
                    SwalReact.showValidationMessage("Informe o nome do cliente.");
                    return false;
                }
                return { name, phone };
            },
            didClose: () => setCancelFocusAddInput(false),
        });

        if (!result.isConfirmed || !result.value) return;
        const customer = new Customer(v4(), result.value.name, result.value.phone);
        await customerRepository.save(customer);
        await customerCreditService.ensureAccount(customer.id);
        sell.setCustomerRef(customer.id, customer.getName());
        await sellRepository.save(sell);
        await loadCustomers();
    }, [customers.length, loadCustomers, moduleAccess.limits.customersLimit, sell]);


    const cancelSell = useCallback(() => {
        if (!sell) return;
        sellRepository.remove(sell);
        closeSell();
    }, [sell, closeSell]);

    const applyDiscount = useCallback((percent: number) => {
        if (!sell) return;
        const clamped = Math.min(Math.max(percent, 1), 99);
        const factor = 1 - clamped / 100;
        sell.getProducts().getValues().forEach((p) => {
            p.price = Math.round(p.price * factor * 100) / 100;
            sell.changeProduct(p);
        });
        sellRepository.save(sell);
    }, [sell]);

    const markAsCredit = useCallback(() => {
        if (!sell) return;
        sell.setPaymentMethod(SellPaymentMethod.CREDIT);
        sellRepository.save(sell);
    }, [sell]);

    const removeLastItem = useCallback(() => {
        if (!sell) return;
        const products = sell.getProducts().getValues();
        if (products.length === 0) return;
        sell.removeProduct(products[products.length - 1].id);
        sellRepository.save(sell);
    }, [sell]);

    const clearCart = useCallback(() => {
        if (!sell) return;
        sell.getProducts().getValues().forEach((p) => sell.removeProduct(p.id));
        sellRepository.save(sell);
    }, [sell]);

    const duplicateLastItem = useCallback(() => {
        if (!sell) return;
        const products = sell.getProducts().getValues();
        if (products.length === 0) return;
        const last = products[products.length - 1];
        sell.incrementAmount(last.id);
        sellRepository.save(sell);
    }, [sell]);

    const roundTotal = useCallback((target: number) => {
        if (!sell) return;
        const products = sell.getProducts().getValues();
        if (products.length === 0) return;
        const currentTotal = sell.getSellTotal();
        if (target <= currentTotal) return;
        const diff = target - currentTotal;
        const last = products[products.length - 1];
        last.price = Math.round((last.price + diff / last.quantity) * 100) / 100;
        sell.changeProduct(last);
        sellRepository.save(sell);
    }, [sell]);

    const changeProductPrice = useCallback(async (productId: string) => {
        if (!sell) return;

        const product = sell.getProduct(productId);
        if (!product) return;

        const newMoney = new ValueObjectObservable(product.price);
        setCancelFocusAddInput(true);

        const changeValueModal = await SwalReact.mixin({
            title: 'Mudar valor',
            html: (
                <MoneyInputValueObject title="Novo Valor" valueObject={newMoney} textFieldProps={{
                    fullWidth: true,
                    sx: { mt: 2 },
                    autoFocus: true,
                    onKeyUp: (event) => {
                        if (event.key === 'Enter') {
                            changeValueModal.clickConfirm();
                        }
                    }
                }} />
            ),
            showCancelButton: true,
            confirmButtonText: `Salvar`,
            cancelButtonText: `Cancelar`,
            icon: 'info'
        });

        const result = await changeValueModal.fire();

        if (result.isConfirmed) {
            product.price = newMoney.get();
            sell.changeProduct(product);
            sellRepository.save(sell);
        }

        setCancelFocusAddInput(false);
    }, [sell]);

    const addOnlyPrice = useCallback(async () => {
        if (!sell) return;

        const price = new ValueObjectObservable(0);
        setCancelFocusAddInput(true);

        const changeValueModal = SwalReact.mixin({
            title: 'Venda avulsa',
            html: (
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <TextField
                        id="onlyPriceDesc"
                        label="Descrição (Opcional)"
                        placeholder="Ex: Caderno"
                        fullWidth
                    />
                    <MoneyInputValueObject title="Valor" valueObject={price} textFieldProps={{
                        fullWidth: true,
                        autoFocus: true,
                        onKeyDown: (event) => {
                            if (event.key === 'Enter') {
                                changeValueModal.clickConfirm();
                            }
                        }
                    }} />
                </Stack>
            ),
            showCancelButton: true,
            confirmButtonText: `Salvar`,
            cancelButtonText: `Cancelar`,
            icon: 'info'
        });

        const result = await changeValueModal.fire();

        if (result.isConfirmed) {
            const descInput = (document.getElementById('onlyPriceDesc') as HTMLInputElement)?.value?.trim();
            const desc = descInput ? descInput : 'Produto avulso';
            const newProduct = new SellProduct(
                new SellUnregisterProduct(v4(), desc, price.get(), noImage),
                1,
                price.get()
            );
            sell.addProduct(newProduct);
            sellRepository.save(sell);
        }

        setCancelFocusAddInput(false);
    }, [sell]);

    const addUnregisteredProduct = useCallback((name: string, priceValue: number) => {
        if (!sell) return;
        const newProduct = new SellProduct(
            new SellUnregisterProduct(v4(), name, priceValue, noImage),
            1,
            priceValue
        );
        sell.addProduct(newProduct);
        sellRepository.save(sell);
    }, [sell]);

    const openQuickRegisterModal = useCallback((searchTerm: string) => {
        if (!sell) return;
        const prefill = inferRegisterPrefillFromSearch(searchTerm);
        setCancelFocusAddInput(true);
        const addProductSellModal = SwalReact.mixin({
            title: "📦 Cadastrar e adicionar",
            text: "Preencha os dados ou use só o valor.",
            width: "min(100%, 28rem)",
            html: (
                <AddProductSell
                    key={searchTerm}
                    initialName={prefill.initialName}
                    initialCode={prefill.initialCode}
                    addOnlyPrice={() => {
                        setLoading(false);
                        addProductSellModal.close();
                        addOnlyPrice();
                    }}
                />
            ),
            focusConfirm: false,
            preConfirm: () => {
                const name = (document.getElementById("name") as HTMLInputElement)?.value;
                const code =
                    (document.getElementById("productCode") as HTMLInputElement)?.value?.trim() ?? "";
                const price = (document.getElementById("amount") as HTMLInputElement)?.dataset.amount;
                const category = (document.getElementById("categoryId") as HTMLInputElement)?.value;

                if (!name || !price || !category) {
                    (document.getElementById("amountError") as HTMLElement).style.opacity = "1";
                    return false;
                }

                return [name, price, category, code];
            },
            didClose: () => {
                setCancelFocusAddInput(false);
                setLoading(false);
            },
        });

        void addProductSellModal.fire().then(async (result) => {
            const formValues = result.value as [string, string, string, string] | undefined;
            if (!formValues || !sell) return;
            setLoading(true);
            try {
                let category = null;
                if (formValues[2]) {
                    category = await productCategoriesRepository.findById(formValues[2] as string);
                }
                const price = parseFloat(formValues[1] as string);
                const newProduct = new Product(
                    v4(),
                    formValues[0] || "",
                    Number.isFinite(price) ? price : 0,
                    noImage,
                    category
                );
                const codeField = (formValues[3] ?? "").trim();
                if (codeField) {
                    newProduct.setCode(codeField);
                }
                await productRepository.save(newProduct);
                sell.addProduct(new SellProduct(newProduct, 1));
                await sellRepository.save(sell);
            } finally {
                setLoading(false);
            }
        });
    }, [sell, addOnlyPrice]);

    const readWithCamera = async () => {
        setCancelFocusAddInput(true);

        const cameraModal = SwalReact.mixin({
            html: (
                <Box sx={{ width: '400px', height: '300px', position: 'relative' }}>
                    <BarcodeScanner onDetected={(result) => {
                        addProduct(result);
                        cameraModal.close();
                    }} />
                </Box>
            ),
            showConfirmButton: false,
            showCancelButton: true,
            cancelButtonText: 'Cancelar',
            allowOutsideClick: true,
            willClose: () => setCancelFocusAddInput(false),
            width: '450px'
        });

        cameraModal.fire();
    };

    useEffect(() => {
        const keyEvent = (event: KeyboardEvent) => {
            if (cancelFocusAddInput) return;
            if (!sell) return;

            if (event.key === 'F2') {
                finishSell();
                return;
            }

            if (event.key === 'F3') {
                if (hasProductsCatalog) {
                    saveSell();
                }
                return;
            }

            if (event.key === 'F9') {
                cancelSell();
                return;
            }

            if (event.key === 'Delete') {
                setRemoveMode(removeMode => !!!removeMode);
                return;
            }

            if (!isNaN(parseInt(event.key)) && !cancelFocusAddInput) {
                const element = document.getElementById('smart-sell-input') as any;

                if (!element) return;
                element.focus();
                return;
            }
        };

        document.addEventListener('keydown', keyEvent);

        return () => document.removeEventListener('keydown', keyEvent);
    }, [cancelFocusAddInput, sell, setRemoveMode, cancelSell, finishSell, saveSell, hasProductsCatalog]);

    const sellSpeedDial = useMemo(
        () => {
            const actions: Array<{
                title: string;
                icon: JSX.Element;
                tooltipOpen: boolean;
                color: "inherit" | "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning";
                onClick: () => void;
            }> = [
                    {
                        title: "✅ Finalizar",
                        icon: <CheckCircle />,
                        tooltipOpen: true,
                        color: "success",
                        onClick: () => finishSell(),
                    },
                ];
            if (hasProductsCatalog) {
                actions.push({
                    title: "💾 Salvar",
                    icon: <Save />,
                    tooltipOpen: true,
                    color: "info",
                    onClick: () => saveSell(),
                });
            }
            actions.push({
                title: "❌ Cancelar",
                icon: <Cancel />,
                tooltipOpen: true,
                color: "error",
                onClick: () => cancelSell(),
            });
            return actions;
        },
        [hasProductsCatalog, finishSell, saveSell, cancelSell]
    );

    if (!sell) {
        return (
            <Backdrop sx={{ color: "#fff", zIndex: (t) => t.zIndex.modal }} open>
                <CircularProgress color="inherit" />
            </Backdrop>
        );
    }

    if (loading) {
        return (
            <Backdrop sx={{ color: "#fff", zIndex: (t) => t.zIndex.modal }} open>
                <CircularProgress color="inherit" />
            </Backdrop>
        );
    }

    if (isFinishSell) {
        return (
            <Shell title="🧾 Finalizar venda">
                <FinishSellComponent
                    sell={sell}
                    cashSessionId={cashSessionId}
                    onCompleteSell={() => {
                        //pass
                    }} onClose={() => setIsFinishSell(false)} closeSell={() => closeSell()} />
            </Shell>
        );
    }

    return (
        <Shell title="🛒 Vender" speedDialOnlySize={750} speedDial={sellSpeedDial}>
            <>
                <Stack id="sellPage" spacing={2} sx={{ pb: 11, mr: { lg: `${COPILOT_PANEL_WIDTH + 16}px` } }}>
                    {removeMode ? (
                        <Alert severity="warning" variant="outlined">
                            Modo remoção: informe o código do item para retirar da venda. Pressione{" "}
                            <strong>Delete</strong> de novo para voltar a adicionar.
                        </Alert>
                    ) : null}

                    <Card elevation={0} sx={{ border: 1, borderColor: "divider", overflow: "visible" }}>
                        <CardContent sx={{ overflow: "visible" }}>
                            <SmartSellInput
                                sellId={sell.id}
                                removeMode={removeMode}
                                customers={customers}
                                onAddByCode={(code) => addProduct(code)}
                                onAddProductById={addProductById}
                                onSearchMore={(term) => { setPickTerm(term); setPickOpen(true); }}
                                onRegisterNew={(term) => openQuickRegisterModal(term)}
                                onAddOnlyPrice={() => addOnlyPrice()}
                                onAddUnregistered={(name, price) => addUnregisteredProduct(name, price)}
                                onFocusLock={(locked) => setCancelFocusAddInput(locked)}
                                onSelectCustomer={async (customer) => {
                                    sell.setCustomerRef(customer.id, customer.getName());
                                    await sellRepository.save(sell);
                                }}
                            />
                        </CardContent>
                    </Card>

                    {moduleAccess.activeCustomersTier ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Autocomplete
                            size="small"
                            options={customers}
                            value={customers.find((customer) => customer.id === sell.getCustomerId()) || null}
                            getOptionLabel={(customer) =>
                                `${customer.getName()}${customer.getPhone() ? ` · ${customer.getPhone()}` : ""}`
                            }
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            noOptionsText="Nenhum cliente encontrado"
                            onChange={async (_event, customer) => {
                                if (!customer) sell.clearCustomerRef();
                                else sell.setCustomerRef(customer.id, customer.getName());
                                await sellRepository.save(sell);
                            }}
                            sx={{ flex: 1 }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Cliente (opcional)"
                                    size="small"
                                    fullWidth
                                />
                            )}
                        />
                        {canCreateCustomerInSell ? (
                            <Button
                                size="small"
                                variant="text"
                                onClick={() => quickCreateCustomer()}
                                sx={{ whiteSpace: "nowrap", fontSize: 12, color: "text.secondary" }}
                            >
                                + Novo
                            </Button>
                        ) : null}
                    </Box>
                    ) : null}

                    <Card elevation={0} sx={{ border: 1, borderColor: "divider", overflow: "hidden" }}>
                        <Box sx={{ px: 2, py: 1.5, bgcolor: (t) => alpha(t.palette.grey[500], 0.06), borderBottom: 1, borderColor: "divider" }}>
                            <Typography variant="subtitle1" fontWeight={700}>Itens na venda</Typography>
                        </Box>

                        {sell.getProducts().getValues().length === 0 ? (
                            <Box sx={{ py: 5, display: "flex", flexDirection: "column", alignItems: "center" }}>
                                <Typography color="text.disabled" variant="body2">Nenhum item adicionado</Typography>
                            </Box>
                        ) : (
                            <>
                                {sell.getProducts().getValues().map((product, idx) => (
                                    <Box
                                        key={product.id}
                                        sx={{
                                            px: 2,
                                            py: 1.5,
                                            borderTop: idx > 0 ? 1 : 0,
                                            borderColor: "divider",
                                        }}
                                    >
                                        <Stack direction="row" spacing={1.5} alignItems="flex-start">
                                            <Avatar
                                                src={product.entity.getImage()}
                                                sx={{ width: 40, height: 40, mt: 0.5, flexShrink: 0 }}
                                            />
                                            <Box flex={1} minWidth={0}>
                                                <Typography fontWeight={700} noWrap>
                                                    {product.entity.getName() || "Produto sem título"}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {product.entity instanceof SellUnregisterProduct
                                                        ? "Produto não cadastrado"
                                                        : product.entity.getCodeLabelForUi()}
                                                </Typography>
                                                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.75 }}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => { e.preventDefault(); sell.decrementAmount(product.id); sellRepository.save(sell); }}
                                                    >
                                                        <RemoveCircleOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                    <Chip label={product.quantity} size="small" sx={{ minWidth: 32, fontWeight: 700 }} />
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => { e.preventDefault(); sell.incrementAmount(product.id); sellRepository.save(sell); }}
                                                    >
                                                        <AddCircleOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        onClick={(e) => { e.preventDefault(); changeProductPrice(product.id); }}
                                                    >
                                                        <Edit fontSize="small" />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={(e) => { e.preventDefault(); sell.removeProduct(product.id); sellRepository.save(sell); }}
                                                    >
                                                        <Delete fontSize="small" />
                                                    </IconButton>
                                                </Stack>
                                            </Box>
                                            <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                                                <Typography fontWeight={800} color="primary.main">
                                                    {(product.price * product.quantity).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {product.price.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })} cada
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </Box>
                                ))}

                                <Box
                                    sx={{
                                        px: 2,
                                        py: 1.5,
                                        borderTop: 1,
                                        borderColor: "divider",
                                        bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                    }}
                                >
                                    <Typography variant="body2" color="text.secondary" fontWeight={600}>
                                        Total da venda
                                    </Typography>
                                    <Typography variant="h5" fontWeight={800} color="primary.main">
                                        {sell.getSellTotal().toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                                    </Typography>
                                </Box>
                            </>
                        )}
                    </Card>
                </Stack>

                <Box
                    sx={{
                        position: "fixed",
                        bottom: 0,
                        left: { xs: 0, md: APP_DRAWER_WIDTH },
                        right: { xs: 0, lg: COPILOT_PANEL_WIDTH },
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                        py: 1.5,
                        px: { xs: 1.5, md: 2 },
                        bgcolor: "background.paper",
                        borderTop: 1,
                        borderColor: "divider",
                        boxShadow: "0 -2px 12px rgba(0,0,0,0.06)",
                        zIndex: (t) => t.zIndex.appBar,
                    }}
                >
                    <Button
                        variant="text"
                        color="error"
                        startIcon={<Cancel />}
                        onClick={() => cancelSell()}
                        sx={{ fontWeight: 700 }}
                    >
                        Cancelar
                        <Box component="span" sx={{ display: { xs: "none", md: "inline" }, ml: 0.75, opacity: 0.5, fontSize: "0.7rem" }}>F9</Box>
                    </Button>

                    <Stack direction="row" spacing={1} alignItems="center">
                        {hasProductsCatalog && (
                            <Button
                                variant="outlined"
                                startIcon={<Save />}
                                onClick={() => saveSell()}
                                sx={{ fontWeight: 700 }}
                            >
                                Salvar
                                <Box component="span" sx={{ display: { xs: "none", md: "inline" }, ml: 0.75, opacity: 0.5, fontSize: "0.7rem" }}>F3</Box>
                            </Button>
                        )}
                        <Button
                            variant="contained"
                            color="success"
                            size="large"
                            startIcon={<CheckCircle />}
                            onClick={() => finishSell()}
                            sx={{ fontWeight: 800 }}
                        >
                            Finalizar
                            <Box component="span" sx={{ display: { xs: "none", md: "inline" }, ml: 0.75, opacity: 0.7, fontSize: "0.7rem" }}>F2</Box>
                        </Button>
                    </Stack>
                </Box>

                <SellCopilotPanel
                    sell={sell}
                    onAddProductById={async (productId) => { await addProduct(productId); }}
                    onApplyDiscount={applyDiscount}
                    onMarkAsCredit={markAsCredit}
                    onRemoveLastItem={removeLastItem}
                    onClearCart={clearCart}
                    onDuplicateLastItem={duplicateLastItem}
                    onRoundTotal={roundTotal}
                />

                <SellProductPickerDialog
                    open={pickOpen}
                    initialTerm={pickTerm}
                    onClose={() => {
                        setPickOpen(false);
                        setCancelFocusAddInput(false);
                    }}
                    onPick={(p) => {
                        if (!sell) return;
                        sell.addProduct(new SellProduct(p, 1));
                        void sellRepository.save(sell);
                        setCancelFocusAddInput(false);
                    }}
                    onRegisterNew={(term) => {
                        setPickOpen(false);
                        openQuickRegisterModal(term);
                    }}
                />
            </>
        </Shell>
    )
};
